import { api } from './client'

/**
 * F-088 (TASK-0342): script di chiamata, questionari di qualificazione, disponibilità degli operatori e
 * operatori delle liste. Le configurazioni sono solo Admin; lo script e il questionario di un lead si leggono
 * da chi vede il lead (stesso gate ADR-008 della scheda lead: lead non visibile = 404).
 */

export type CustomerType = 'person' | 'company'

export const CUSTOMER_TYPE_LABEL: Record<CustomerType, string> = {
  person: 'Persona',
  company: 'Azienda',
}

// ---------------------------------------------------------------- Script

export interface CallScript {
  id: number
  name: string
  body: string
  campaignId: number | null
  sector: string | null
  customerType: CustomerType | null
  /** F-143 (REQ-092-001): operatore e prodotto del catalogo (id senza FK). */
  catalogOperatorId?: number | null
  catalogProductId?: number | null
  priority: number
  isActive: boolean
  createdAt: string
  updatedAt: string | null
}

export interface CallScriptBody {
  name: string
  body: string
  campaignId?: number | null
  sector?: string | null
  customerType?: CustomerType | null
  catalogOperatorId?: number | null
  catalogProductId?: number | null
  priority?: number
  isActive?: boolean
}

export type LeadScriptSource = 'script' | 'campaign' | 'none'

export interface LeadCallScript {
  leadId: number
  /** `script` = script scelto per campagna/settore/tipo cliente; `campaign` = testo libero della campagna; `none`. */
  source: LeadScriptSource
  scriptId: number | null
  name: string | null
  body: string | null
  campaignId: number | null
  campaignName: string | null
  catalogProductId?: number | null
  catalogOperatorId?: number | null
}

// ---------------------------------------------------------------- Questionari

export type QuestionType = 'Testo' | 'Numero' | 'Data' | 'SiNo' | 'Elenco'

export const QUESTION_TYPES: { value: QuestionType; label: string }[] = [
  { value: 'Testo', label: 'Testo' },
  { value: 'Numero', label: 'Numero' },
  { value: 'Data', label: 'Data' },
  { value: 'SiNo', label: 'Sì / No' },
  { value: 'Elenco', label: 'Elenco di opzioni' },
]

export interface QualificationQuestion {
  code: string
  label: string
  type: QuestionType
  options?: string[] | null
  required?: boolean
  /** La domanda compare solo se la risposta alla domanda `code` è uguale a `equals`. */
  showIf?: { code: string; equals: string } | null
}

export type FormStatus = 'Bozza' | 'Attivo' | 'Archiviato'

export interface QualificationForm {
  id: number
  name: string
  sector: string | null
  customerType: CustomerType | null
  questions: QualificationQuestion[]
  version: number
  status: FormStatus
  activatedAt: string | null
  createdAt: string
  updatedAt: string | null
}

export interface QualificationFormBody {
  name: string
  sector?: string | null
  customerType?: CustomerType | null
  questions: QualificationQuestion[]
}

export interface LeadQualificationAnswerSet {
  formId: number
  formName: string | null
  formVersion: number
  questions: QualificationQuestion[] | null
  answers: Record<string, string>
  answeredBy: number | null
  answeredAt: string
}

export interface LeadQualification {
  leadId: number
  /** Il questionario attivo applicabile al lead (settore, poi tipo cliente); null se non ce n'è. */
  form: {
    id: number
    name: string
    version: number
    sector: string | null
    customerType: CustomerType | null
    questions: QualificationQuestion[]
  } | null
  answers: LeadQualificationAnswerSet[]
}

/** Errore 400 del salvataggio: il primo messaggio è in `error`, l'elenco completo in `errors`. */
export interface ContractQualificationItem {
  code: string
  label: string
  type: string
  value: string
}

export interface ContractLeadQualification {
  contractId: number
  leads: {
    leadId: number
    forms: {
      formId: number
      formName: string | null
      formVersion: number
      answeredAt: string
      items: ContractQualificationItem[]
    }[]
  }[]
}

// ---------------------------------------------------------------- Disponibilità

export type WeekdayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'

export const WEEKDAYS: { key: WeekdayKey; label: string }[] = [
  { key: 'mon', label: 'Lunedì' },
  { key: 'tue', label: 'Martedì' },
  { key: 'wed', label: 'Mercoledì' },
  { key: 'thu', label: 'Giovedì' },
  { key: 'fri', label: 'Venerdì' },
  { key: 'sat', label: 'Sabato' },
  { key: 'sun', label: 'Domenica' },
]

export interface TimeSlot {
  from: string
  to: string
}

export type WeeklySchedule = Partial<Record<WeekdayKey, TimeSlot[]>>

export type AbsenceReason = 'Ferie' | 'Malattia' | 'Altro'

export const ABSENCE_REASONS: AbsenceReason[] = ['Ferie', 'Malattia', 'Altro']

export interface AgentAvailabilityRow {
  userId: number
  weeklySchedule: WeeklySchedule
  updatedAt: string | null
}

export interface AgentAbsence {
  id: number
  userId: number
  from: string
  to: string
  reason: AbsenceReason
}

export interface AgentAvailabilityOverview {
  availabilities: AgentAvailabilityRow[]
  absences: AgentAbsence[]
}

export interface AgentAvailabilityMe {
  userId: number
  availability: AgentAvailabilityRow | null
  absences: AgentAbsence[]
}

// ---------------------------------------------------------------- Ritentativi (F-097)

export type RetryPolicyStatus = 'Bozza' | 'Attiva' | 'Disattivata'

/** Cosa succede a un lead che ha esaurito i tentativi senza risposta. */
export type RetryExhaustionAction = 'none' | 'lost_unreachable' | 'recovery_list'

export const RETRY_ACTIONS: { value: RetryExhaustionAction; label: string; hint: string }[] = [
  { value: 'none', label: 'Nessuna azione', hint: 'Il lead resta in coda come "tentativi esauriti, da valutare".' },
  { value: 'lost_unreachable', label: 'Perso, irraggiungibile', hint: 'Il lead passa a Perso con motivo "Irraggiungibile".' },
  { value: 'recovery_list', label: 'Lista di recupero', hint: 'Il lead entra anche nella campagna di recupero; la sua campagna non cambia.' },
]

export interface LeadRetryPolicy {
  id: number
  name: string
  /** Campagna a cui si applica; null = politica predefinita. */
  campaignId: number | null
  /** Minuti di attesa prima del tentativo 1, 2, ... */
  delaysMinutes: number[]
  exhaustionAction: RetryExhaustionAction
  recoveryCampaignId: number | null
  status: RetryPolicyStatus
  reason: string | null
  activatedAt: string | null
  createdAt: string
  updatedAt: string | null
}

export interface LeadRetryPolicyList {
  statuses: RetryPolicyStatus[]
  actions: RetryExhaustionAction[]
  /** Il piano predefinito, valido quando nessuna politica è attiva (provvisorio). */
  defaultSchedule: { provisional: boolean; maxAttempts: number; delaysMinutes: number[] }
  items: LeadRetryPolicy[]
}

export interface LeadRetryPolicyBody {
  name: string
  campaignId: number | null
  delaysMinutes: number[]
  exhaustionAction: RetryExhaustionAction
  recoveryCampaignId: number | null
}

export interface LeadRetryPreview {
  policyId: number
  maxAttempts: number
  exhaustedCount: number
  actionCount: number
  action: RetryExhaustionAction
  leadIds: number[]
  sampleSize: number
}

// ---------------------------------------------------------------- Tempi di contatto (F-143)

export interface LeadSpeedTarget {
  id: number
  sourceCode: string
  label: string
  minutes: number
  confirmed: boolean
  /** Nessuna riga in tabella: vale il numero predefinito. */
  isDefault: boolean
  updatedAt: string | null
}

export interface LeadSpeedTargets {
  targets: LeadSpeedTarget[]
  /** Vero finché almeno una fonte non è confermata. */
  provisional: boolean
}

// ---------------------------------------------------------------- Client

export const callCenterSetupApi = {
  scripts: {
    list: () => api<CallScript[]>('/api/call-scripts'),
    create: (data: CallScriptBody) =>
      api<CallScript>('/api/call-scripts', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: CallScriptBody) =>
      api<CallScript>(`/api/call-scripts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    remove: (id: number) => api<void>(`/api/call-scripts/${id}`, { method: 'DELETE' }),
    /** `productId` (F-143): il prodotto scelto dalla postazione, oltre a quelli delle campagne del lead. */
    forLead: (leadId: number, productId?: number) =>
      api<LeadCallScript>(`/api/leads/${leadId}/call-script${productId ? `?productId=${productId}` : ''}`),
  },
  /** F-143 (REQ-073-001): tempi di primo contatto per fonte. Lettura staff, scrittura Admin. */
  speedTargets: {
    get: () => api<LeadSpeedTargets>('/api/leads/speed-targets'),
    put: (targets: { sourceCode: string; minutes: number; confirmed: boolean }[]) =>
      api<LeadSpeedTargets>('/api/leads/speed-targets', { method: 'PUT', body: JSON.stringify({ targets }) }),
  },
  forms: {
    list: (status?: FormStatus) =>
      api<QualificationForm[]>(`/api/lead-qualification-forms${status ? `?status=${status}` : ''}`),
    create: (data: QualificationFormBody) =>
      api<QualificationForm>('/api/lead-qualification-forms', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: QualificationFormBody) =>
      api<QualificationForm>(`/api/lead-qualification-forms/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    activate: (id: number) =>
      api<QualificationForm>(`/api/lead-qualification-forms/${id}/activate`, { method: 'POST', body: '{}' }),
    archive: (id: number) =>
      api<QualificationForm>(`/api/lead-qualification-forms/${id}/archive`, { method: 'POST', body: '{}' }),
    newVersion: (id: number) =>
      api<QualificationForm>(`/api/lead-qualification-forms/${id}/new-version`, { method: 'POST', body: '{}' }),
    remove: (id: number) => api<void>(`/api/lead-qualification-forms/${id}`, { method: 'DELETE' }),
  },
  qualification: {
    forLead: (leadId: number) => api<LeadQualification>(`/api/leads/${leadId}/qualification`),
    save: (leadId: number, formId: number, answers: Record<string, string>) =>
      api<{ leadId: number; formId: number; formVersion: number; answers: Record<string, string> }>(
        `/api/leads/${leadId}/qualification`,
        { method: 'PUT', body: JSON.stringify({ formId, answers }) },
      ),
    forContract: (contractId: number) =>
      api<ContractLeadQualification>(`/api/contracts/${contractId}/lead-qualification`),
  },
  availability: {
    overview: () => api<AgentAvailabilityOverview>('/api/agent-availability'),
    me: () => api<AgentAvailabilityMe>('/api/agent-availability/me'),
    /** Orario senza fasce = nessun vincolo (la riga si elimina). */
    setSchedule: (userId: number, weeklySchedule: WeeklySchedule) =>
      api<{ userId: number; availability: AgentAvailabilityRow | null }>(
        `/api/agent-availability/${userId}`,
        { method: 'PUT', body: JSON.stringify({ weeklySchedule }) },
      ),
    addAbsence: (data: { userId: number; from: string; to: string; reason: AbsenceReason }) =>
      api<AgentAbsence>('/api/agent-availability/absences', { method: 'POST', body: JSON.stringify(data) }),
    removeAbsence: (id: number) =>
      api<void>(`/api/agent-availability/absences/${id}`, { method: 'DELETE' }),
  },
  retryPolicies: {
    list: () => api<LeadRetryPolicyList>('/api/lead-retry-policies'),
    create: (data: LeadRetryPolicyBody) =>
      api<LeadRetryPolicy>('/api/lead-retry-policies', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: LeadRetryPolicyBody) =>
      api<LeadRetryPolicy>(`/api/lead-retry-policies/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    remove: (id: number) => api<void>(`/api/lead-retry-policies/${id}`, { method: 'DELETE' }),
    preview: (id: number) => api<LeadRetryPreview>(`/api/lead-retry-policies/${id}/preview`),
    activate: (id: number, reason: string) =>
      api<LeadRetryPolicy>(`/api/lead-retry-policies/${id}/activate`, { method: 'POST', body: JSON.stringify({ reason }) }),
    deactivate: (id: number, reason?: string) =>
      api<LeadRetryPolicy>(`/api/lead-retry-policies/${id}/deactivate`, { method: 'POST', body: JSON.stringify({ reason: reason ?? null }) }),
  },
  campaignOperators: {
    get: (campaignId: number) =>
      api<{ campaignId: number; userIds: number[] }>(`/api/campaigns/${campaignId}/operators`),
    replace: (campaignId: number, userIds: number[]) =>
      api<{ campaignId: number; userIds: number[] }>(
        `/api/campaigns/${campaignId}/operators`,
        { method: 'PUT', body: JSON.stringify({ userIds }) },
      ),
  },
}
